import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, CreditCard, ShoppingCart, ArrowRight } from 'lucide-react';

interface PlatformCard {
  id: string;
  icon: React.ElementType;
  title: string;
  description: string;
  features: string[];
  image: string;
  href: string;
  color: string;
}

const platforms: PlatformCard[] = [
  {
    id: 'articles',
    icon: FileText,
    title: '资讯中心',
    description: '汇聚行业动态、政策解读、市场分析等优质内容，助力企业把握市场脉搏。',
    features: ['行业资讯', '政策解读', '市场分析', '专家观点'],
    image: 'https://www.cdkmzs.com/uploads/platform/0703/cdkmzs/service4.png',
    href: '/articles',
    color: 'bg-blue-500'
  },
  {
    id: 'insurance',
    icon: CreditCard,
    title: '票据平台',
    description: '提供便捷的票据服务，支持电子票据流转、贴现融资等全流程在线操作。',
    features: ['电子票据', '在线贴现', '融资服务', '安全可靠'],
    image: 'https://www.cdkmzs.com/uploads/platform/0703/cdkmzs/service5.png',
    href: 'https://zxkf-web.aero-credit.com/login',
    color: 'bg-purple-500'
  },
  {
    id: 'shop',
    icon: ShoppingCart,
    title: '采购平台',
    description: '一站式工业品采购平台，覆盖钢材、铝材、不锈钢等多品类，源头直供。',
    features: ['品类齐全', '品质保证', '源头直供', '快速交付'],
    image: 'https://www.cdkmzs.com/uploads/platform/0703/cdkmzs/service6.png',
    href: 'https://swueecg.com/#/enterpriseZoneDetail?id=2049001811704049665&isQingxin=',
    color: 'bg-green-500'
  }
];

export default function PlatformCards() {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 bg-white relative overflow-hidden border-t border-gray-50">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-50 rounded-full blur-3xl opacity-60" />
        <div className="absolute bottom-20 right-10 w-80 h-80 bg-purple-50 rounded-full blur-3xl opacity-60" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div
          className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <span className="inline-block px-4 py-1.5 bg-[#1c5bff]/10 text-[#1c5bff] rounded-full text-sm font-medium mb-4">
            平台矩阵
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            多元化平台，一站式服务
          </h2>
          <p className="text-gray-500 text-lg">
            整合资讯、票据、采购三大核心平台，构建完整的数字化服务生态
          </p>
        </div>

        {/* Platform Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {platforms.map((platform, index) => (
            <div
              key={platform.id}
              className={`group relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 border border-gray-100 hover:border-[#1c5bff]/30 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
              }`}
              style={{
                transitionDelay: `${index * 100}ms`
              }}
            >
              {/* Image */}
              <div className="relative h-56 overflow-hidden">
                <img
                  src={platform.image}
                  alt={platform.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                
                {/* Icon Badge */}
                <div className={`absolute top-4 left-4 w-12 h-12 ${platform.color} rounded-xl flex items-center justify-center shadow-lg z-20 transition-transform duration-300 group-hover:scale-110`}>
                  <platform.icon className="w-6 h-6 text-white" />
                </div>

                {/* Category Label */}
                <div className="absolute top-4 right-4 px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-xs font-medium text-gray-700">
                  核心平台
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#1c5bff] transition-colors">
                  {platform.title}
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed mb-4">
                  {platform.description}
                </p>

                {/* Features */}
                <ul className="space-y-2 mb-6">
                  {platform.features.map((feature, i) => (
                    <li
                      key={i}
                      className="flex items-center gap-2 text-sm text-gray-500"
                    >
                      <span className="w-1.5 h-1.5 bg-[#1c5bff] rounded-full" />
                      {feature}
                    </li>
                  ))}
                </ul>

                {/* CTA Link */}
                <Link
                  to={platform.href}
                  className="inline-flex items-center gap-2 text-sm font-semibold text-gray-700 group/link hover:text-[#1c5bff] transition-colors"
                >
                  <span className="relative">
                    立即体验
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#1c5bff] transition-all duration-300 group-hover/link:w-full" />
                  </span>
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover/link:translate-x-1" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}